import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import EditorialMarquee from "@/components/EditorialMarquee";

const CATEGORY_ORDER = [
  "Stacks",
  "Recovery",
  "Growth",
  "Metabolic",
  "Anti-Aging",
  "Cognitive",
  "Wellness",
  "Supplies",
];

export default function PriceList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data.filter((p) => !p.external_url && !["sfh-3", "sfh-tz"].includes(p.id))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const groups = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: products.filter((p) => p.category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <main className="pt-24">
      <section className="mx-auto max-w-5xl px-6 pb-24 pt-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-mono text-[11px] tracking-[0.35em] text-[#00ff72]"
        >
          EFFECTIVE 08-01-26 — FOR RESEARCH PURPOSES ONLY
        </motion.p>
        <h1 className="mt-3 overflow-hidden font-display text-6xl tracking-tight text-[#F9F6F0] md:text-8xl">
          <motion.span
            className="block"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          >
            Price <span className="italic text-[#666666]">List</span>
          </motion.span>
        </h1>
        <p className="mt-6 max-w-lg text-sm leading-relaxed text-[#A3A3A3]">
          All prices are per single vial — stacks are 2-vial kits. Every vial is
          lyophilized, HPLC-verified and ships with full documentation. Click any item to view
          details or add it to your cart.
        </p>

        {loading ? (
          <div className="mt-14 space-y-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse border border-white/10 bg-[#111111]" />
            ))}
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.cat} data-testid={`price-group-${g.cat.toLowerCase().replace(/\s+/g, "-")}`} className="mt-14">
              <div className="flex items-baseline justify-between border-b-2 border-[#00ff72]/40 pb-3">
                <h2 className="font-mono text-xs tracking-[0.35em] text-[#00ff72]">
                  {g.cat.toUpperCase()}
                </h2>
                <span className="font-mono text-[10px] tracking-widest text-[#666666]">
                  {g.items.length} {g.items.length === 1 ? "ITEM" : "ITEMS"}
                </span>
              </div>
              <ul>
                {g.items.map((p, i) => (
                  <motion.li
                    key={p.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.3) }}
                  >
                    <Link
                      to={`/products/${p.id}`}
                      data-testid={`price-row-${p.id}`}
                      className="group flex items-center gap-5 border-b border-white/10 py-5 transition-colors hover:bg-white/[0.03]"
                    >
                      <img
                        src={p.image}
                        alt={p.name}
                        loading="lazy"
                        className="h-16 w-16 shrink-0 border border-white/10 object-cover grayscale transition-[filter] duration-500 group-hover:grayscale-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-xl text-[#F9F6F0] transition-colors group-hover:text-[#00ff72] md:text-2xl">
                          {p.name}
                        </p>
                        <p className="truncate font-mono text-[10px] tracking-widest text-[#666666]">
                          {p.sequence}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        {p.dosages.map((d) => (
                          <p key={d.label} className="font-mono text-xs text-[#A3A3A3] md:text-sm">
                            {d.label} — <span className="text-[#00ff72]">${d.price.toFixed(2)}</span>
                          </p>
                        ))}
                      </div>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </div>
          ))
        )}

        <p className="mt-14 border border-red-500/30 bg-red-500/5 p-4 font-mono text-[10px] leading-relaxed tracking-wider text-red-400/90">
          FOR RESEARCH PURPOSES ONLY. NOT FOR HUMAN OR VETERINARY CONSUMPTION, DIAGNOSTIC OR
          THERAPEUTIC USE. PRICES SUBJECT TO CHANGE — QUESTIONS: INFO@SFHBIOHEALTHANDWELLNESS.COM
        </p>
      </section>
      <EditorialMarquee />
    </main>
  );
}
